/**
 * The navigation model — one declaration, driving both the sidebar and the route metadata.
 *
 * Because both read this, a nav item and its route cannot disagree about who may use it. A
 * salesperson opening the dashboard sees their profile and their sales, and nothing else — not a
 * greyed-out item that fails on click, which is worse than an absent one because it reads as a
 * bug rather than a boundary.
 *
 * Each item declares the phase its screen lands in, and anything above `CURRENT_PHASE` is filtered
 * out rather than deleted — so the shape of the product stays visible here before it is built, and
 * shipping a screen is raising one number rather than reconstructing a nav entry from memory.
 */

import { Permission } from '@pos/shared';

/**
 * Sidebar sections, in the order they appear. Selling first because that is what the shop does
 * all day; "You" last because a person's own profile is the thing they look for least often.
 */
export const NAV_GROUPS = [
  'Home',
  'Selling',
  'Online store',
  'Money',
  'Inventory',
  'Purchasing',
  'Oversight',
  'Setup',
  'You',
] as const;

export type NavGroup = (typeof NAV_GROUPS)[number];

export type NavItem = {
  readonly to: string;
  readonly label: string;
  readonly icon: string;
  /** Every listed permission is required. Absent means everyone signed in. */
  readonly permission?: string | readonly string[];
  readonly group: NavGroup;
  /** Phase the screen lands in. Anything above 2 is not rendered yet. */
  readonly phase: number;
};

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/home', label: 'Home', icon: 'home', group: 'Home', phase: 3 },

  { to: '/sales', label: 'Sales', icon: 'receipt', permission: Permission.SaleViewAll, group: 'Selling', phase: 2 },
  // The online store: orders first, because an order is a customer waiting for a call.
  { to: '/online/orders', label: 'Orders', icon: 'online-orders', permission: Permission.OnlineOrderView, group: 'Online store', phase: 6 },
  { to: '/online/listings', label: 'Listings', icon: 'online-listings', permission: Permission.OnlineView, group: 'Online store', phase: 6 },
  { to: '/online/settings', label: 'Storefront', icon: 'online-settings', permission: Permission.OnlineStorefrontManage, group: 'Online store', phase: 6 },

  { to: '/payments', label: 'Payments', icon: 'card', permission: Permission.PaymentView, group: 'Money', phase: 3 },
  { to: '/payments/unmatched', label: 'Unmatched', icon: 'unmatched', permission: Permission.PaymentUnmatchedView, group: 'Money', phase: 3 },
  { to: '/reconciliation', label: 'Reconciliation', icon: 'scales', permission: Permission.PaymentReconcileView, group: 'Money', phase: 3 },
  { to: '/cash', label: 'Cash', icon: 'cash', permission: Permission.CashViewPosition, group: 'Money', phase: 3 },

  { to: '/me/sales', label: 'My sales', icon: 'person-receipt', permission: Permission.SaleViewOwn, group: 'You', phase: 2 },
  { to: '/profile', label: 'Profile', icon: 'person', group: 'You', phase: 2 },
  // No permission: the manual explains how the shop works, not what it earns, and the person
  // who most needs it is the one with the fewest permissions.
  { to: '/help', label: 'Help', icon: 'help', group: 'You', phase: 2 },

  { to: '/stock', label: 'Stock', icon: 'stock', permission: [Permission.StockView, Permission.CostView], group: 'Inventory', phase: 3 },
  { to: '/categories', label: 'Categories', icon: 'categories', permission: Permission.CategoryView, group: 'Inventory', phase: 3 },
  { to: '/products', label: 'Products', icon: 'products', permission: Permission.ProductView, group: 'Inventory', phase: 3 },
  { to: '/variants', label: 'Items', icon: 'variants', permission: Permission.ProductView, group: 'Inventory', phase: 3 },
  // Business-wide by design — see FindUnitBySerialQuery's own summary on the server: an IMEI
  // lookup answers where a handset is, not just what the active shop holds.
  { to: '/units/lookup', label: 'Find a unit', icon: 'find-unit', permission: [Permission.UnitView, Permission.CostView], group: 'Inventory', phase: 6 },
  { to: '/suppliers', label: 'Suppliers', icon: 'suppliers', permission: Permission.SupplierView, group: 'Purchasing', phase: 3 },
  { to: '/purchases', label: 'Purchases', icon: 'purchases', permission: [Permission.PurchaseView, Permission.CostView], group: 'Purchasing', phase: 3 },
  { to: '/imports', label: 'Imports', icon: 'upload', permission: Permission.ImportDryRun, group: 'Setup', phase: 3 },
  // Phase 1 scope (auth/device security): user CRUD, deactivation, password reset, and device
  // revocation. Not Phase 4 — that covers unrelated screens (catalogue, pricing, stock).
  { to: '/users', label: 'Users', icon: 'people', permission: Permission.UserView, group: 'Setup', phase: 3 },
  { to: '/roles', label: 'Roles', icon: 'shield', permission: Permission.RoleView, group: 'Setup', phase: 3 },
  { to: '/shops', label: 'Shops', icon: 'storefront', permission: Permission.ShopView, group: 'Setup', phase: 3 },
  { to: '/audit', label: 'Audit trail', icon: 'history', permission: Permission.AuditView, group: 'Setup', phase: 3 },

  // ── Phase 4: stock integrity ──────────────────────────────────────────────
  // Gated on the *view* permission only. Every screen below is readable by anyone who may see it
  // and acts only where the actor also holds the narrower permission — so a manager who may
  // receive a transfer but not approve a transit loss still gets the screen, minus that button.
  //
  // Claims sit under Purchasing rather than Inventory because a claim is a conversation with a
  // supplier; the stock is only what it is about.
  { to: '/transfers', label: 'Transfers', icon: 'transfers', permission: Permission.TransferView, group: 'Inventory', phase: 4 },
  { to: '/stock-counts', label: 'Stock counts', icon: 'counts', permission: Permission.StockCountView, group: 'Inventory', phase: 4 },
  { to: '/faulty-stock', label: 'Faulty stock', icon: 'faulty', permission: [Permission.WriteOffView, Permission.CostView], group: 'Inventory', phase: 4 },
  { to: '/write-offs', label: 'Write-offs', icon: 'write-offs', permission: [Permission.WriteOffView, Permission.CostView], group: 'Inventory', phase: 4 },
  { to: '/settlements', label: 'Settlements', icon: 'settlements', permission: Permission.SettlementView, group: 'Inventory', phase: 4 },
  { to: '/prices/bulk', label: 'Bulk prices', icon: 'price-bulk', permission: Permission.PriceBulkExport, group: 'Inventory', phase: 4 },
  { to: '/claims', label: 'Supplier claims', icon: 'claims', permission: [Permission.ClaimView, Permission.CostView], group: 'Purchasing', phase: 4 },

  // ── Phase 5: oversight ─────────────────────────────────────────────────────
  // review.view is a manager/owner-level permission; a salesperson never sees this group at
  // all, matching "no margin, no cost, no other people's figures."
  { to: '/review', label: 'Review queue', icon: 'review', permission: Permission.ReviewView, group: 'Oversight', phase: 5 },
  // Disputes gets no permission at all — dispute.create is granted to every role, and the list
  // itself is what lets a salesperson track their own raised disputes.
  { to: '/disputes', label: 'Disputes', icon: 'disputes', permission: Permission.DisputeCreate, group: 'Oversight', phase: 5 },
  { to: '/approvals', label: 'Approvals', icon: 'approvals', permission: Permission.ApprovalDecide, group: 'Oversight', phase: 5 },
  { to: '/brokers', label: 'Brokers', icon: 'brokers', permission: Permission.BrokerView, group: 'Oversight', phase: 5 },
  { to: '/customers', label: 'Customers', icon: 'customers', permission: Permission.CustomerView, group: 'Oversight', phase: 5 },
  { to: '/reports', label: 'Reports', icon: 'reports', permission: Permission.ReportView, group: 'Oversight', phase: 5 },

  // ── Phase 6: printing and notifications ───────────────────────────────────
  // Gated on printer.manage rather than printjob.view — the agents/printers screen is a setup
  // action (register hardware), not a monitoring one, so it belongs to whoever may change what is
  // registered. The queue itself gates on the narrower printjob.view, since watching it needs no
  // ability to change hardware.
  { to: '/print-agents', label: 'Print agents', icon: 'print-agent', permission: Permission.PrinterManage, group: 'Setup', phase: 6 },
  { to: '/print-jobs', label: 'Print jobs', icon: 'print-jobs', permission: Permission.PrintJobView, group: 'Setup', phase: 6 },
  { to: '/notifications', label: 'Notifications', icon: 'notifications', permission: Permission.NotificationManage, group: 'Setup', phase: 6 },
];

/** What phase this build ships. Raised as the later screens land. */
export const CURRENT_PHASE = 6;

export const shippedItems = NAV_ITEMS.filter((item) => item.phase <= CURRENT_PHASE);
